import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Sun, Moon } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { WEDDING_STYLES, slug } from "../../lib/site";

const EMPTY = { couple_names: "", email: "", phone: "", socials: "", style: "", event_date: "", ceremony_location: "", reception_location: "", timing: "", guests: "", budget: "", colors: "", notes: "", referral: "" };
const STEPS = ["Voi", "Il vostro stile", "L'evento", "Budget & note"];
const REFERRALS = ["Instagram", "Passaparola", "Google", "Wedding planner", "Sono passati in negozio", "Altro"];

const Field = ({ label, children, req }) => (
  <label className="block">
    <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ash">{label}{req && <span className="text-terra"> *</span>}</span>
    <div className="mt-2">{children}</div>
  </label>
);

export const WeddingForm = () => {
  const [step, setStep] = useState(0);
  const [f, setF] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const inp = (k, props = {}) => <input data-testid={`wedding-form-${k.replace(/_/g, "-")}-input`} className="field" value={f[k]} onChange={set(k)} {...props} />;

  const validStep = () => {
    if (step === 0) {
      if (!f.couple_names.trim() || !f.email.trim() || !f.phone.trim()) { toast.error("Inserite nomi, email e telefono"); return false; }
      if (!/^\S+@\S+\.\S+$/.test(f.email)) { toast.error("Email non valida"); return false; }
    }
    return true;
  };
  const next = () => validStep() && setStep((s) => Math.min(s + 1, 3));
  const submit = async () => {
    setSending(true);
    try {
      await api.post("/inquiries", f);
      setDone(true);
      toast.success("Richiesta inviata! Vi risponderò al più presto.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <div data-testid="wedding-form-success" className="bg-cypress text-cream p-10 sm:p-14">
        <span className="w-14 h-14 rounded-full bg-cream text-cypress flex items-center justify-center"><Check className="w-6 h-6" /></span>
        <h3 className="mt-8 font-serif text-4xl sm:text-5xl leading-tight">Grazie, {f.couple_names.split(/[ &e]/)[0] || "cari sposi"}!</h3>
        <p className="mt-4 text-cream/85 max-w-md">Ho ricevuto la vostra richiesta. Vi contatterò presto per iniziare a disegnare insieme il vostro progetto floreale.</p>
        <button type="button" data-testid="wedding-form-reset" onClick={() => { setF(EMPTY); setStep(0); setDone(false); }} className="mt-8 link-underline text-sm">Invia un'altra richiesta</button>
      </div>
    );
  }

  return (
    <div data-testid="wedding-form" className="bg-white/70 border border-stone p-6 sm:p-10">
      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className="flex-1">
            <div className="h-[3px] bg-stone overflow-hidden"><motion.div className="h-full bg-cypress" initial={false} animate={{ width: i <= step ? "100%" : "0%" }} transition={{ duration: 0.6 }} /></div>
            <p className={`mt-2 hidden sm:block font-mono text-[10px] uppercase tracking-[0.2em] ${i === step ? "text-cypress" : "text-ash"}`}>{s}</p>
          </div>
        ))}
      </div>
      <p className="mt-6 font-mono text-xs text-terra">Passo {step + 1} di 4</p>
      <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className="mt-4 space-y-6 min-h-[340px]">
        {step === 0 && (<>
          <h3 className="font-serif text-3xl sm:text-4xl text-ink">Chi siete?</h3>
          <Field label="Nomi della coppia" req>{inp("couple_names", { placeholder: "Es. Giulia & Marco" })}</Field>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Email" req>{inp("email", { type: "email", placeholder: "nome@email.it" })}</Field>
            <Field label="Telefono" req>{inp("phone", { type: "tel", placeholder: "+39 …" })}</Field>
          </div>
          <Field label="I vostri social">{inp("socials", { placeholder: "Instagram, Facebook, TikTok…" })}</Field>
        </>)}
        {step === 1 && (<>
          <h3 className="font-serif text-3xl sm:text-4xl text-ink">Come vi definite?</h3>
          <div className="grid grid-cols-2 gap-3">
            {WEDDING_STYLES.map((s) => (
              <button key={s.key} type="button" data-testid={`wedding-form-style-${slug(s.key)}`} onClick={() => set("style")(s.key)}
                className={`group relative overflow-hidden aspect-[4/3] text-left border-2 transition-colors ${f.style === s.key ? "border-cypress" : "border-transparent"}`}>
                <img src={s.img} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <span className="absolute inset-0 bg-gradient-to-t from-ink/75 to-transparent" />
                <span className="absolute bottom-3 left-3 right-3 font-serif text-lg sm:text-2xl text-cream leading-tight">{s.key}</span>
                {f.style === s.key && <span className="absolute top-3 right-3 w-7 h-7 rounded-full bg-cream text-cypress flex items-center justify-center"><Check className="w-4 h-4" /></span>}
              </button>
            ))}
          </div>
          <Field label="Colori preferiti per l'evento">{inp("colors", { placeholder: "Es. salvia, avorio, terracotta" })}</Field>
        </>)}
        {step === 2 && (<>
          <h3 className="font-serif text-3xl sm:text-4xl text-ink">Il grande giorno</h3>
          <div className="grid sm:grid-cols-2 gap-6">
            <Field label="Data dell'evento">{inp("event_date", { type: "date" })}</Field>
            <Field label="Numero di ospiti">{inp("guests", { type: "number", min: 1, placeholder: "Es. 120" })}</Field>
          </div>
          <Field label="Luogo della cerimonia">{inp("ceremony_location", { placeholder: "Chiesa, comune, all'aperto…" })}</Field>
          <Field label="Luogo del ricevimento">{inp("reception_location", { placeholder: "Masseria, villa, ristorante…" })}</Field>
          <Field label="Matrimonio diurno o serale?">
            <div className="grid grid-cols-2 gap-3">
              {[["Diurno", Sun], ["Serale", Moon]].map(([t, Icon]) => (
                <button key={t} type="button" data-testid={`wedding-form-timing-${t.toLowerCase()}`} onClick={() => set("timing")(t)}
                  className={`flex items-center justify-center gap-2 py-4 border transition-colors ${f.timing === t ? "bg-cypress text-cream border-cypress" : "border-stone text-ink hover:border-cypress"}`}>
                  <Icon className="w-4 h-4" /> {t}
                </button>
              ))}
            </div>
          </Field>
        </>)}
        {step === 3 && (<>
          <h3 className="font-serif text-3xl sm:text-4xl text-ink">Ultimi dettagli</h3>
          <Field label="Budget indicativo per il progetto floreale">{inp("budget", { placeholder: "Es. 3.000 €" })}</Field>
          <Field label="Come mi avete conosciuta?">
            <select data-testid="wedding-form-referral-select" className="field" value={f.referral} onChange={set("referral")}>
              <option value="">Seleziona…</option>
              {REFERRALS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Note in più">
            <textarea data-testid="wedding-form-notes-input" rows={4} className="field resize-none" value={f.notes} onChange={set("notes")} placeholder="Raccontatemi tutto quello che vi viene in mente…" />
          </Field>
        </>)}
      </motion.div>
      <div className="mt-8 flex items-center justify-between gap-4">
        <button type="button" data-testid="wedding-form-back" onClick={() => setStep((s) => s - 1)} disabled={step === 0} className="inline-flex items-center gap-2 text-sm text-ink disabled:opacity-30"><ArrowLeft className="w-4 h-4" /> Indietro</button>
        {step < 3 ? (
          <button type="button" data-testid={`wedding-form-step-${step + 1}-next`} onClick={next} className="inline-flex items-center gap-2 rounded-full bg-cypress text-cream px-6 py-3 text-sm hover:bg-terra transition-colors">Avanti <ArrowRight className="w-4 h-4" /></button>
        ) : (
          <button type="button" data-testid="wedding-form-submit-button" onClick={submit} disabled={sending} className="inline-flex items-center gap-2 rounded-full bg-terra text-cream px-6 py-3 text-sm hover:bg-cypress transition-colors disabled:opacity-60">{sending ? "Invio…" : "Invia la richiesta"} <ArrowRight className="w-4 h-4" /></button>
        )}
      </div>
    </div>
  );
};
